<script setup>
import SmartBox from '../common/SmartBox.vue';
import SearchInput from './components/SearchInput.vue';
import SearchEmpty from './components/SearchEmpty.vue';
import Occupancy from './components/Occupancy.vue';
import BookerCalendar from './components/BookerCalendar.vue';
import { ref, computed, getCurrentInstance, onMounted, nextTick } from 'vue';

const { proxy } = getCurrentInstance()
const __ = proxy.__

const fn = window.__pt.fn;
const config = window.__pt.settings.site;
const settings = window.__pt.settings;
const MAX_SEARCH_RESULTS = 10;
const defaultDate = fn.getDate();
const format = window.__pt.settings.formatBoxDate;

const guestsBox = ref(null);
const calendarBox = ref(null);
const isMobile = ref(false);
const openAirportSearch = ref(false);
const loadingAirports = ref(false);
const openHotelSearch = ref(false);
const loadingHotels = ref(false);
const airportMobileInput = ref(null);
const hotelMobileInput = ref(null);

const TRANSFER_TYPES = {
  ROUNDTRIP: 'Roundtrip',
  AIRPORT_TO_HOTEL: 'airportToHotel',
  HOTEL_TO_AIRPORT: 'hotelToAirport',
};

const form = ref({
  transferType: TRANSFER_TYPES.ROUNDTRIP,
  airport: '',
  hotel: '',
  dates: '',
  guests: '',
});

const fieldErrors = ref({
  airport: '',
  hotel: '',
  dates: '',
  guests: '',
});

const isRoundtrip = computed(() => form.value.transferType === TRANSFER_TYPES.ROUNDTRIP);
const isHotelToAirport = computed(() => form.value.transferType === TRANSFER_TYPES.HOTEL_TO_AIRPORT);

function focusMobileInput(inputRef) {
  if (!isMobile.value) return;

  nextTick(() => {
    inputRef.value?.focus();
  });
}

function onOpenAirportSearch() {
  focusMobileInput(airportMobileInput);
}

function onOpenHotelSearch() {
  focusMobileInput(hotelMobileInput);
}

const checkIn = ref(null);
const checkOut = ref(null);

function getPrequoteConfig() {
  return fn.mobileAndTabletCheck()
    ? config.configDaysPrequoteMobile
    : config.configDaysPrequoteDesktop;
}

function initializeDefaultDates() {
  const prequote = getPrequoteConfig();
  const startDate = new Date(defaultDate);

  startDate.setDate(startDate.getDate() + prequote.weeksPrequote * 7);
  startDate.setDate(startDate.getDate() - startDate.getDay() + prequote.startDate);

  checkIn.value = fn.getDate(fn.formatDate(startDate, settings.formatDate));
  checkOut.value = fn.getDate(fn.formatDate(startDate, settings.formatDate));
  checkOut.value.setDate(checkOut.value.getDate() + prequote.daysPrequote);
}

const settingPaxes = {
  minAdults: 1,
  maxAdults: 8,
  maxChildren: 6,
  maxInfants: 4,
  minAge: 0,
  maxAge: 17,
};
const paxes = ref({ adults: 1, children: 0, infants: 0 });

const calendarDates = computed({
  get() {
    return {
      start: checkIn.value ? new Date(checkIn.value) : null,
      end: checkOut.value ? new Date(checkOut.value) : null
    };
  },
  set(val) {
    checkIn.value = val.start ? new Date(val.start) : null;
    checkOut.value = isRoundtrip.value && val.end ? new Date(val.end) : null;
    updateFormDates(checkIn.value, checkOut.value);
  }
});

function updateFormDates(start, end) {
  if (!start) return;
  if (isRoundtrip.value && end) {
    form.value.dates = `${dayjs(start).format(format).replace(/\./g, '')} - ${dayjs(end).format(format).replace(/\./g, '')}`;
  } else {
    form.value.dates = `${dayjs(start).format(format).replace(/\./g, '')}`;
  }
}

function onSelectDates(val) {
  checkIn.value = val.start;
  checkOut.value = isRoundtrip.value ? val.end : null;
  updateFormDates(checkIn.value, checkOut.value);
  if (calendarBox.value) {
    calendarBox.value.close();
  }
}

function onChangeTransferType() {
  if (!isRoundtrip.value) {
    checkOut.value = null;
  } else if (checkIn.value && !checkOut.value) {
    const prequote = getPrequoteConfig();
    checkOut.value = new Date(checkIn.value);
    checkOut.value.setDate(checkOut.value.getDate() + prequote.daysPrequote);
  }
  updateFormDates(checkIn.value, checkOut.value);
}

// Airport/hotel suggestion services aren't configured yet — wiring pending.
const airportResults = ref([]);
const hotelResults = ref([]);
const selectedAirport = ref(null);
const selectedHotel = ref(null);
const searchedAirport = ref(false);
const searchedHotel = ref(false);

const showEmptyAirport = computed(() => searchedAirport.value && !loadingAirports.value && airportResults.value.length === 0);
const showEmptyHotel = computed(() => searchedHotel.value && !loadingHotels.value && hotelResults.value.length === 0);

function getAirportLabel(item) {
  if (typeof item === 'string') return item;

  const name = item?.displayName || item?.name || item?.airportName || item?.locationName || item?.description || '';
  const code = item?.code || item?.airportCode || item?.iataCode || '';

  return code && !name.toLowerCase().includes(code.toLowerCase()) ? `${name} (${code})` : name;
}

async function onSearchAirport(value, mobile = false) {
  form.value.airport = value;
  fieldErrors.value.airport = '';
  selectedAirport.value = null;
  if (!isHotelToAirport.value) {
    selectedHotel.value = null;
    form.value.hotel = '';
    hotelResults.value = [];
    searchedHotel.value = false;
    openHotelSearch.value = false;
  }

  if (!value || value.trim().length < 3) {
    airportResults.value = [];
    searchedAirport.value = false;
    if (!mobile) openAirportSearch.value = false;
    return;
  }

  loadingAirports.value = true;
  if (!mobile) openAirportSearch.value = false;

  try {
    const response = await axios.get(config.endPoints.transferLocationsUrl, {
      params: { Query: value }
    });
    const data = response?.data;
    const results = Array.isArray(data) ? data : data?.results || data?.items || [];
    airportResults.value = results.slice(0, MAX_SEARCH_RESULTS);
    openAirportSearch.value = true;
  } catch (error) {
    console.log(error);
    airportResults.value = [];
    if (!mobile) openAirportSearch.value = false;
  } finally {
    loadingAirports.value = false;
    searchedAirport.value = true;
  }
}

function onSelectAirport(item) {
  selectedAirport.value = item;
  form.value.airport = getAirportLabel(item);
  if (!isHotelToAirport.value) {
    selectedHotel.value = null;
    form.value.hotel = '';
    hotelResults.value = [];
  }
  openAirportSearch.value = false;
}

function getHotelLabel(item) {
  if (typeof item === 'string') return item;

  return item?.display || item?.displayName || item?.name || item?.hotelName || item?.description || '';
}

async function onSearchHotel(value, mobile = false) {
  form.value.hotel = value;
  fieldErrors.value.hotel = '';
  selectedHotel.value = null;

  if (!value || value.trim().length < 3) {
    hotelResults.value = [];
    searchedHotel.value = false;
    if (!mobile) openHotelSearch.value = false;
    return;
  }

  const params = { Query: value };
  if (selectedAirport.value?.id != null) {
    params.airportId = selectedAirport.value.placeId;
  }

  loadingHotels.value = true;
  if (!mobile) openHotelSearch.value = false;

  try {
    const response = await axios.get(config.endPoints.transferHotelsUrl, { params });
    const data = response?.data;
    const results = Array.isArray(data) ? data : data?.results || data?.items || [];
    hotelResults.value = results.slice(0, MAX_SEARCH_RESULTS);
    openHotelSearch.value = true;
  } catch (error) {
    console.log(error);
    hotelResults.value = [];
    if (!mobile) openHotelSearch.value = false;
  } finally {
    loadingHotels.value = false;
    searchedHotel.value = true;
  }
}

function onSelectHotel(item) {
  selectedHotel.value = item;
  form.value.hotel = getHotelLabel(item);
  openHotelSearch.value = false;
}

function onChangePaxes(payload) {
  paxes.value = payload || paxes.value;
  updateGuestsLabel();
}
function onApplyPaxes(payload) {
  paxes.value = payload || paxes.value;
  updateGuestsLabel();
  if (guestsBox.value && typeof guestsBox.value.close === 'function') {
    guestsBox.value.close();
  }
}
function onCancelPaxes() {
  updateGuestsLabel();
  if (guestsBox.value && typeof guestsBox.value.close === 'function') {
    guestsBox.value.close();
  }
}
function onCloseGuests() {
  updateGuestsLabel();
}
function updateGuestsLabel() {
  const total = (paxes.value.adults || 0) + (paxes.value.children || 0) + (paxes.value.infants || 0);
  form.value.guests = `${total} ${__('booker.passengers')}`;
}

// Legacy "que-hacer" widget that validates/resolves the search and hands back
// where + how to post the browser to the (still legacy) results page.
const TRANSFER_RESULTS_PATH = '/que-hacer/traslados/resultados';
const submitting = ref(false);
const submitError = ref('');

function getAirportId(item) {
  return item?.airportId ?? item?.placeId ?? item?.id ?? '';
}
function getAirportCode(item) {
  return item?.code ?? item?.airportCode ?? item?.iataCode ?? '';
}
function getHotelId(item) {
  return item?.hotelId ?? item?.placeId ?? item?.id ?? '';
}

function validateForm() {
  fieldErrors.value.airport = selectedAirport.value ? '' : __('booker.select_origin');
  fieldErrors.value.hotel = selectedHotel.value ? '' : __('booker.select_destination');
  fieldErrors.value.dates = (checkIn.value && (!isRoundtrip.value || checkOut.value)) ? '' : __('booker.select_dates');

  return !fieldErrors.value.airport && !fieldErrors.value.hotel && !fieldErrors.value.dates;
}

function submitSearch() {
  if (!validateForm()) return;

  submitError.value = '';
  submitting.value = true;
  document.getElementById('loader-page')?.classList.remove('d-none');

  const params = {
    TransferType: form.value.transferType,
    'DepartureAirport.AirportId': getAirportId(selectedAirport.value),
    'DepartureAirport.Code': getAirportCode(selectedAirport.value),
    HotelDestinationName: form.value.hotel,
    HotelDestinationId: getHotelId(selectedHotel.value),
    HotelDestinationType: '',
    TransferChannelId: 0,
    StartDate: dayjs(checkIn.value).format('YYYY/MM/DD'),
    EndDate: isRoundtrip.value && checkOut.value ? dayjs(checkOut.value).format('YYYY/MM/DD') : '',
    Adults: paxes.value.adults,
    Minors: paxes.value.children || 0,
    Infants: paxes.value.infants || 0,
    SourceHost: '',
    SiteType: 'None',
    'Searchbox.PostUrl': TRANSFER_RESULTS_PATH,
    'Searchbox.IsModal': true,
    'Searchbox.FormMethod': 'Post',
    WhiteLabelId: '',
  };

  jsonp(config.endPoints.transferSubmitUrl, params, { callbackParam: 'jsoncallback' })
    .then((response) => {
      if (response?.Errors?.length) {
        submitError.value = response.Errors.join(' ');
        document.getElementById('loader-page')?.classList.add('d-none');
        return;
      }
      navigateToResults(response);
    })
    .catch((error) => {
      console.log(error);
      submitError.value = __('booker.transfer_submit_error');
      document.getElementById('loader-page')?.classList.add('d-none');
    })
    .finally(() => {
      submitting.value = false;
    });
}

// Plain script-tag JSONP — jQuery isn't guaranteed to ship with $.ajax in this bundle,
// and this is the same cross-domain transport the legacy "jsonpBooker" endpoint expects.
function jsonp(url, params, { callbackParam = 'callback', timeoutMs = 10000 } = {}) {
  return new Promise((resolve, reject) => {
    const callbackName = `__transferSubmitCallback_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
    const script = document.createElement('script');
    let timeoutId;

    const cleanup = () => {
      clearTimeout(timeoutId);
      delete window[callbackName];
      script.remove();
    };

    window[callbackName] = (data) => {
      cleanup();
      resolve(data);
    };

    script.onerror = () => {
      cleanup();
      reject(new Error('jsonp request failed'));
    };
    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error('jsonp request timed out'));
    }, timeoutMs);

    const query = new URLSearchParams({ ...params, [callbackParam]: callbackName }).toString();
    script.src = `${url}?${query}`;
    document.body.appendChild(script);
  });
}

function navigateToResults(response) {
  const formEl = document.createElement('form');
  formEl.setAttribute('method', response.Method === 1 ? 'post' : 'get');
  formEl.setAttribute('action', `${config.tourSiteUrl.replace(/\/$/, '')}${response.Url}`);

  Object.entries(response.FormData || {}).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    const hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', value);
    formEl.appendChild(hiddenField);
  });

  document.body.appendChild(formEl);
  formEl.submit();
}

onMounted(() => {
  initializeDefaultDates();
  updateFormDates(checkIn.value, checkOut.value);
  updateGuestsLabel();
});
</script>

<template>
  <form class="search" @submit.prevent="submitSearch" :aria-label="__('booker.transfers')">
    <div class="search__inputs">
      <SmartBox class="search__airport" :class="{ 'search__field--second': isHotelToAirport }"
        :title="__('booker.airport_sub')" :OpenBox="openAirportSearch" :isHeaderActive="false"
        :hasContent="airportResults.length > 0 || showEmptyAirport" @update:OpenBox="openAirportSearch = $event"
        @isMobile="isMobile = $event" @open="onOpenAirportSearch">
        <template #trigger>
          <SearchInput id="transfer-airport" :label="__('booker.airport')" :selectOnFocus="!isMobile"
            :activeFocus="!isMobile" :placeholder="__('booker.airport_placeholder')" v-model="form.airport"
            @input="onSearchAirport" :debounce="500" :autocomplete="'off'" :spellcheck="false"
            :error="!!fieldErrors.airport" :errorMessage="fieldErrors.airport" :readonly="isMobile">
            <template #icon>
              <span class="icon font-icons icons-plane-airport font-20"></span>
            </template>
          </SearchInput>
        </template>
        <template #content>
          <div class="searcher">
            <div v-if="isMobile" class="searcher__head">
              <SearchInput ref="airportMobileInput" id="transfer-airport-mobile" :label="__('booker.airport')"
                :selectOnFocus="true"
                :activeFocus="true" :placeholder="__('booker.airport_placeholder')" v-model="form.airport"
                @input="onSearchAirport($event, true)" :debounce="500" :autocomplete="'off'" :spellcheck="false">
                <template #icon>
                  <span class="icon font-icons icons-plane-airport font-20"></span>
                </template>
              </SearchInput>
            </div>
            <ul v-if="airportResults.length > 0" class="searcher__list">
              <li v-for="(item, index) in airportResults" :key="item.id || item.code || `airport-${index}`"
                class="searcher__item">
                <button class="searcher__action" type="button" @click="onSelectAirport(item)">
                  <span class="searcher__icon icons-plane-airport font-20" aria-hidden="true"></span>
                  <span class="searcher__label">{{ getAirportLabel(item) }}</span>
                </button>
              </li>
            </ul>
            <SearchEmpty v-else-if="showEmptyAirport" :title="__('booker.no_results_title')"
              :text="__('booker.no_results_hint', [form.airport])" />
          </div>
        </template>
      </SmartBox>

      <SmartBox class="search__hotel" :class="{ 'search__field--first': isHotelToAirport }"
        :title="__('booker.hotel_sub')" :OpenBox="openHotelSearch" :isHeaderActive="false"
        :hasContent="hotelResults.length > 0 || showEmptyHotel" @update:OpenBox="openHotelSearch = $event"
        @isMobile="isMobile = $event" @open="onOpenHotelSearch">
        <template #trigger>
          <SearchInput id="transfer-hotel" :label="__('booker.hotel')" :selectOnFocus="!isMobile"
            :activeFocus="!isMobile" :placeholder="__('booker.hotel_placeholder')" v-model="form.hotel"
            @input="onSearchHotel" :debounce="500" :autocomplete="'off'" :spellcheck="false"
            :error="!!fieldErrors.hotel" :errorMessage="fieldErrors.hotel" :readonly="isMobile">
            <template #icon>
              <span class="icon font-icons icons-hotel font-20"></span>
            </template>
          </SearchInput>
        </template>
        <template #content>
          <div class="searcher">
            <div v-if="isMobile" class="searcher__head">
              <SearchInput ref="hotelMobileInput" id="transfer-hotel-mobile" :label="__('booker.hotel')"
                :selectOnFocus="true"
                :activeFocus="true" :placeholder="__('booker.hotel_placeholder')" v-model="form.hotel"
                @input="onSearchHotel($event, true)" :debounce="500" :autocomplete="'off'" :spellcheck="false">
                <template #icon>
                  <span class="icon font-icons icons-hotel font-20"></span>
                </template>
              </SearchInput>
            </div>
            <ul v-if="hotelResults.length > 0" class="searcher__list">
              <li v-for="(item, index) in hotelResults" :key="item.id || item.code || `hotel-${index}`"
                class="searcher__item">
                <button class="searcher__action" type="button" @click="onSelectHotel(item)">
                  <span class="searcher__icon icons-hotel font-20" aria-hidden="true"></span>
                  <span class="searcher__label">{{ getHotelLabel(item) }}</span>
                </button>
              </li>
            </ul>
            <SearchEmpty v-else-if="showEmptyHotel" :title="__('booker.no_results_title')"
              :text="__('booker.no_results_hint', [form.hotel])" />
          </div>
        </template>
      </SmartBox>

      <SmartBox ref="calendarBox" :title="isRoundtrip ? __('booker.dates') : __('booker.date')"
        :isHeaderActive="false" :mobile-breakpoint="768">
        <template #trigger>
          <SearchInput id="calendar-checkIn-transfer" type="text" required name="calendar-checkIn-transfer"
            :activeFocus="true" :label="isRoundtrip ? __('booker.dates') : __('booker.date')" :readonly="true"
            v-model="form.dates" :error="!!fieldErrors.dates" :errorMessage="fieldErrors.dates">
            <template #icon>
              <span class="icon font-icons icons-calendar-depart font-20"></span>
            </template>
          </SearchInput>
        </template>
        <template #content>
          <BookerCalendar :modelValue="calendarDates" :currency="config.currency"
            :mode="isRoundtrip ? 'range' : 'single'" :unit="'days'" :minDays="1" :minDate="defaultDate"
            :startLabel="__('booker.departure')" :endLabel="__('booker.return')" @select="onSelectDates"
            @close="calendarBox.close()" />
        </template>
      </SmartBox>

      <SmartBox ref="guestsBox" :title="__('booker.passengers_sub')" :isHeaderActive="false" @close="onCloseGuests"
        :mobile-breakpoint="768">
        <template #trigger>
          <SearchInput id="guests" :label="__('booker.passengers')" :placeholder="form.guests" :readonly="true"
            :activeFocus="true" v-model="form.guests" :error="!!fieldErrors.guests"
            :errorMessage="fieldErrors.guests">
            <template #icon>
              <span class="icon font-icons icons-guests font-20"></span>
            </template>
          </SearchInput>
        </template>
        <template #content>
          <Occupancy :product="'T'" :settingPaxes="settingPaxes" :counts="paxes" @change="onChangePaxes"
            @apply="onApplyPaxes" @cancel="onCancelPaxes">
          </Occupancy>
        </template>
      </SmartBox>
    </div>

    <div class="search__filters">
      <div class="group">
        <legend class="group__legend font-14 font-400 font-strong">
          {{ __('booker.transfer_type') }}
        </legend>
        <ul class="group__list">
          <li class="group__item">
            <label class="option">
              <input type="radio" class="option__check input--radio" name="transferType"
                :value="TRANSFER_TYPES.ROUNDTRIP" v-model="form.transferType" v-on:change="onChangeTransferType" />
              <span class="option__pill font-12 font-md-14 font-400 font-strong">
                {{ __('booker.transfer_round_trip') }}
              </span>
            </label>
          </li>
          <li class="group__item">
            <label class="option">
              <input type="radio" class="option__check input--radio" name="transferType"
                :value="TRANSFER_TYPES.AIRPORT_TO_HOTEL" v-model="form.transferType"
                v-on:change="onChangeTransferType" />
              <span class="option__pill font-12 font-md-14 font-400 font-strong">
                <span>{{ __('booker.airport') }}</span>
                <span class="option__arrow font-icons icons-arrow-forward" aria-hidden="true"></span>
                <span>{{ __('booker.hotel') }}</span>
              </span>
            </label>
          </li>
          <li class="group__item">
            <label class="option">
              <input type="radio" class="option__check input--radio" name="transferType"
                :value="TRANSFER_TYPES.HOTEL_TO_AIRPORT" v-model="form.transferType"
                v-on:change="onChangeTransferType" />
              <span class="option__pill font-12 font-md-14 font-400 font-strong">
                <span>{{ __('booker.hotel') }}</span>
                <span class="option__arrow font-icons icons-arrow-forward" aria-hidden="true"></span>
                <span>{{ __('booker.airport') }}</span>
              </span>
            </label>
          </li>
        </ul>
      </div>
    </div>

    <div class="search__action">
      <button type="submit" class="search__btn btnPrimary btnPrimary--md font-16 font-500" :disabled="submitting">
        {{ __('booker.button') }}
      </button>
      <p v-if="submitError" class="search__error font-14">{{ submitError }}</p>
    </div>
  </form>
</template>

<style scoped lang="scss">
.search {
  display: flex;
  flex-direction: column;
  gap: var(--space-12);
  padding: var(--space-16);
  border-radius: var(--space-12);
  border: 1px solid var(--border-subtle);
  margin: var(--space-16);
  background-color: var(--bg-base);
  box-shadow: var(--shadow-200);

  @media (min-width: 576px) {
    width: 544px;
    margin: var(--space-16) auto;
  }

  @media (min-width: 768px) {
    width: calc(100% - 32px);
    max-width: none;
    border-radius: 0px 0px var(--space-12) var(--space-12);
    display: grid;
    grid-template-areas:
      'inputs inputs'
      'filters action';
    grid-template-columns: 1fr auto;
    padding-inline: 0px;
    gap: var(--space-16) var(--space-12);
    box-shadow: none;
    margin: 0px auto;
    border: 0px;
  }

  @media (min-width: 1280px) {
    grid-template-areas:
      'inputs inputs'
      'filters action';
    grid-template-columns: 1fr auto;
    width: 100%;
    max-width: 1252px;
    margin: 0px auto;
    padding: var(--space-20);
    border-radius: var(--space-12);
    row-gap: var(--space-12);
    column-gap: var(--space-32);
    border: 1px solid var(--border-subtle);
    box-shadow: var(--shadow-300);
  }

  .icon {
    display: flex;
  }

  &__filters {
    grid-area: filters;
    display: flex;
    align-items: center;
    min-width: 0;
    order: 1;

    @media (min-width: 768px) {
      order: initial;
    }
  }

  &__inputs {
    grid-area: inputs;
    display: flex;
    flex-direction: column;
    gap: var(--space-8);
    order: 2;

    > :nth-child(1) {
      order: 1;
    }

    > :nth-child(2) {
      order: 2;
    }

    > :nth-child(3) {
      order: 3;
    }

    > :nth-child(4) {
      order: 4;
    }

    > .search__field--first {
      order: 1;
    }

    > .search__field--second {
      order: 2;
    }

    @media (min-width: 768px) {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: var(--space-16);
      order: initial;
    }

    @media (min-width: 1280px) {
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: var(--space-24);
    }
  }

  &__action {
    grid-area: action;
    order: 3;

    @media (min-width: 768px) {
      display: flex;
      justify-content: flex-end;
      order: initial;
    }
  }

  &__btn {
    width: 100%;

    @media (min-width: 768px) {
      width: 160px;
    }

    @media (min-width: 1280px) {
      width: 160px;
    }
  }

  &__error {
    color: var(--text-error, #950000);
    margin: var(--space-8) 0 0;
  }
}

.group {
  border: none;
  padding: 0px;
  margin: 0px;

  @media (min-width: 768px) {
    display: flex;
    align-items: center;
    gap: var(--space-12);
  }

  &__legend {
    display: none;
    margin: 0px;

    @media (min-width: 1280px) {
      display: flex;
      width: fit-content;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    gap: var(--space-8);
    margin: 0px;
    padding: 0px;
    list-style: none;

    @media (min-width: 768px) {
      flex-direction: row;
      flex-wrap: nowrap;
      gap: var(--space-8);
    }

    @media (min-width: 1280px) {
      gap: var(--space-12);
    }
  }

  &__item + &__item {
    @media (min-width: 768px) {
      border-left: 1px solid var(--border-subtle);
      padding-left: var(--space-8);
    }

    @media (min-width: 1280px) {
      padding-left: var(--space-12);
    }
  }
}

.option {
  display: flex;
  align-items: center;
  gap: var(--space-8);
  cursor: pointer;
  margin: 0px;
  white-space: nowrap;

  &__pill {
    display: inline-flex;
    align-items: center;
    gap: var(--space-8);
  }

  &__arrow {
    display: inline-flex;
    font-size: 1rem;
  }
}

.searcher {
  display: flex;
  flex-direction: column;
  gap: var(--space-24);
  background-color: var(--bg-base);
  width: 100%;

  @media (min-width: 768px) {
    padding: var(--space-16);
    border-radius: var(--space-16);
    width: calc(100vw - 32px);
    max-width: 460px;
  }

  &__head {
    padding-bottom: var(--space-20);
    border-bottom: 1px solid var(--border-subtle);

    @media (min-width: 768px) {
      display: none;
    }
  }

  &__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
  }

  &__item {
    width: 100%;
    border-bottom: 1px solid var(--border-subtle);
  }

  &__action {
    display: flex;
    align-items: center;
    width: 100%;
    padding: var(--space-8);
    border-radius: var(--space-8);
    gap: var(--space-16);
    background: none;
    border: none;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: var(--bg-level2);
    }
  }

  &__icon {
    width: 35px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    border-radius: var(--space-8);
  }

  &__label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
